import i18n from "@core/i18n";

import type { DraftSummary, SettingDescriptor } from "./types";
import { minExpiresAtDate } from "../../../lib/pollExpiration";

export interface BuildDraftInput {
  title: string;
  selectedDatesCount: number;
  timeSlotsCount: number;
  participantEmailsCount: number;
  advancedSettings: Record<string, any>;
  /** Détermine si l'adresse de copie doit être proposée (cf. resultsCopyEmail
   *  ci-dessous) — un compte connecté a déjà une adresse email, un invité
   *  non. */
  isAuthenticated: boolean;
  /** Dernière date proposée (YYYY-MM-DD) : borne basse de l'expiration. */
  lastSelectedDate?: string | null;
}

/**
 * Construit un `DraftSummary` immutable à partir du state du `PollCreator`.
 * Détermine `isEmpty` et `isDefault` selon les valeurs. Ne calcule PAS les
 * hints — ça reste `useSettingsHints` (PR 2).
 */
export function buildDraftSummary(input: BuildDraftInput): DraftSummary {
  const a = input.advancedSettings ?? {};

  const settings: SettingDescriptor[] = [];

  // Audience ET visibilité des résultats déplacées hors du récap :
  // - audience → chip up-front du PollCreator ;
  // - visibilité → onglet « Accès » de la config avancée (doublon retiré,
  //   feedback intent-controls 2026-07-08).
  // Le récap ne conserve donc que les SUGGESTIONS + les réglages AVANCÉS.

  // Groupe HINT — toujours rendus en read-only PR 1, les hints calculés
  // viendront en PR 2 via `useSettingsHints`.
  const location = (a.location ?? "").trim?.() || "";
  settings.push({
    key: "location",
    group: "hint",
    icon: "📍",
    label: i18n.t("draft_summary.location.label"),
    value: location || null,
    displayValue: location || i18n.t("draft_summary.location.unspecified"),
    isEmpty: location.length === 0,
    type: "string",
  });

  const expiresAt = a.expiresAt as string | undefined | null;
  settings.push({
    key: "expiresAt",
    group: "hint",
    icon: "⏰",
    label: i18n.t("draft_summary.expires_at.label"),
    value: expiresAt ?? null,
    displayValue: expiresAt
      ? formatDateHuman(expiresAt)
      : i18n.t("draft_summary.expires_at.no_limit"),
    isEmpty: !expiresAt,
    type: "date",
    min:
      minExpiresAtDate(input.lastSelectedDate ? [input.lastSelectedDate] : []) ??
      undefined,
  });

  // Groupe AVANCÉ
  //
  // Pour un créateur SANS COMPTE, l'adresse de copie (`resultsCopyEmail`) est
  // attachée EN `nested` de ce switch plutôt que poussée comme row à part :
  // un second réglage séparé plus bas dans la liste lisait comme deux
  // décisions indépendantes, alors qu'il n'y en a qu'une (feedback
  // 2026-08-17) — il n'y a pas d'email de compte à défaut, donc pas de choix
  // "où" à faire une fois "oui" décidé. Un compte connecté a déjà son email
  // et n'a rien de plus à faire ici (pas de `nested` dans ce cas).
  //
  // Le trigger enqueue_vote_notification (20260816123000) exige encore
  // `sendEmailCopy=true` pour lire `resultsCopyEmail` : PollCreator.tsx pose
  // les deux ensemble au commit du champ imbriqué (applyPrePublishSuggestion).
  const resultsCopyEmail = (a.resultsCopyEmail ?? "").trim?.() || "";
  settings.push({
    key: "sendNotifications",
    group: "advanced",
    icon: "📧",
    label: i18n.t("draft_summary.send_notifications.label"),
    value: String(!!a.sendNotifications),
    displayValue: a.sendNotifications
      ? i18n.t("draft_summary.enabled")
      : i18n.t("draft_summary.disabled"),
    isDefault: !!a.sendNotifications, // défaut = activées (2026-07-24)
    isEmpty: false,
    type: "boolean",
    nested: input.isAuthenticated
      ? undefined
      : {
          key: "resultsCopyEmail",
          group: "advanced",
          icon: "✉️",
          label: i18n.t("draft_summary.results_copy_email.label"),
          subDescription: i18n.t("draft_summary.results_copy_email.hint"),
          value: resultsCopyEmail || null,
          displayValue:
            resultsCopyEmail ||
            i18n.t("draft_summary.results_copy_email.unspecified"),
          isEmpty: resultsCopyEmail.length === 0,
          type: "string",
          alwaysOpen: true,
        },
  });

  settings.push({
    key: "requireAuth",
    group: "advanced",
    icon: "🔒",
    label: i18n.t("draft_summary.require_auth.label"),
    value: String(!!a.requireAuth),
    displayValue: a.requireAuth
      ? i18n.t("draft_summary.yes")
      : i18n.t("draft_summary.no"),
    isDefault: !a.requireAuth,
    isEmpty: false,
    type: "boolean",
  });

  // oneResponsePerPerson retiré (décision 2026-07-23) : l'unicité par
  // appareil est déjà garantie par l'upsert (poll_id, stable_fingerprint) —
  // l'option n'avait aucun effet réel. Lecture tolérante conservée côté vote
  // pour les sondages existants.

  settings.push({
    key: "allowEditAfterSubmit",
    group: "advanced",
    icon: "✏️",
    label: i18n.t("draft_summary.allow_edit_after_submit.label"),
    value: String(!!a.allowEditAfterSubmit),
    displayValue: a.allowEditAfterSubmit
      ? i18n.t("draft_summary.allowed")
      : i18n.t("draft_summary.no"),
    isDefault: !!a.allowEditAfterSubmit, // default true → c'est le défaut
    isEmpty: false,
    type: "boolean",
  });

  const maxResponses = a.maxResponses;
  settings.push({
    key: "maxResponses",
    group: "advanced",
    icon: "🔢",
    label: i18n.t("draft_summary.max_responses.label"),
    value: maxResponses != null ? String(maxResponses) : null,
    displayValue:
      maxResponses != null
        ? String(maxResponses)
        : i18n.t("draft_summary.max_responses.unlimited"),
    isEmpty: maxResponses == null,
    type: "string",
  });

  // Visibilité des résultats (réintroduite au récap 2026-07-23 sur demande —
  // l'enforcement serveur existe déjà dans get_poll_responses).
  const resultsVisibility = (a.resultsVisibility as string) || "public";
  settings.push({
    key: "resultsVisibility",
    group: "advanced",
    icon: "👁️",
    label: i18n.t("draft_summary.results_visibility.title"),
    value: resultsVisibility,
    displayValue: i18n.t(
      `draft_summary.results_visibility.${
        resultsVisibility === "creator-only"
          ? "creator_only"
          : resultsVisibility === "voters"
            ? "voters"
            : "public"
      }.label`,
    ),
    isDefault: resultsVisibility === "public",
    isEmpty: false,
    type: "enum",
    options: [
      {
        id: "public",
        icon: "🌍",
        label: i18n.t("draft_summary.results_visibility.public.label"),
        description: i18n.t("draft_summary.results_visibility.public.sub"),
      },
      {
        id: "voters",
        icon: "🗳️",
        label: i18n.t("draft_summary.results_visibility.voters.label"),
        description: i18n.t("draft_summary.results_visibility.voters.sub"),
      },
      {
        id: "creator-only",
        icon: "🔐",
        label: i18n.t("draft_summary.results_visibility.creator_only.label"),
        description: i18n.t(
          "draft_summary.results_visibility.creator_only.sub",
        ),
      },
    ],
  });

  // Nom d'expéditeur personnalisable (Étape 6) — groupe advanced, éditable
  // via StringEditor. Pré-rempli avec le nom du compte au moment de la
  // publication (cf. PollCreator.tsx initialisation).
  const senderName = (a.senderName ?? "").trim?.() || "";
  settings.push({
    key: "senderName",
    group: "advanced",
    icon: "👤",
    label: i18n.t("draft_summary.sender_name.label"),
    subDescription: i18n.t("draft_summary.sender_name.hint"),
    value: senderName || null,
    displayValue: senderName || i18n.t("draft_summary.sender_name.default"),
    isEmpty: senderName.length === 0,
    isDefault: senderName.length === 0,
    type: "string",
  });

  return {
    title: input.title,
    selectedDatesCount: input.selectedDatesCount,
    timeSlotsCount: input.timeSlotsCount,
    participantEmailsCount: input.participantEmailsCount,
    settings,
  };
}

function formatDateHuman(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(i18n.language || "fr", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}
